import { useEffect, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';

import { ButtonBase, Drawer } from '@mui/material';

import { Box, Icon, useTheme } from '@sbv-clients/ui-shared';

import { SideBarMenuItem } from '../sidebar/types';
import { NavIcon, sectionIconName } from './nav-icons';
import { isLinkActive, NavSection, useNavModel } from './use-nav-model';

const SideGroup = ({
  section,
  pathname,
  onNavigate,
}: {
  section: NavSection;
  pathname: string;
  onNavigate: () => void;
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(section.active);
  const hasChildren = section.children.length > 0;

  const rowSx = {
    width: '100%',
    minHeight: 46,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '0 12px',
    borderRadius: '10px',
    justifyContent: 'flex-start',
    textAlign: 'left' as const,
    textDecoration: 'none',
    fontSize: '15px',
    fontWeight: section.active ? 600 : 500,
    color: theme.custom?.brandNavy,
    '&:hover': { background: theme.custom?.shellMenuItemHoverBg },
  };
  const icon = (
    <NavIcon
      name={sectionIconName(section.item.label)}
      size={20}
      sx={{
        color: section.active
          ? theme.colors.primary.main
          : theme.custom?.shellSideIcon,
      }}
    />
  );

  if (!hasChildren) {
    return (
      <ButtonBase
        component={Link}
        to={section.href}
        onClick={onNavigate}
        sx={rowSx}
      >
        {icon}
        <Box component="span" sx={{ flex: 1 }}>
          {section.item.label}
        </Box>
      </ButtonBase>
    );
  }

  return (
    <Box>
      <ButtonBase
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        sx={rowSx}
      >
        {icon}
        <Box component="span" sx={{ flex: 1 }}>
          {section.item.label}
        </Box>
        <NavIcon
          name="chevron"
          size={14}
          sx={{
            color: theme.custom?.tableHeaderText,
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 150ms',
          }}
        />
      </ButtonBase>
      {open && (
        <Box
          sx={{
            margin: '0 0 6px 21px',
            padding: '2px 0 2px 21px',
            borderLeft: `1px solid ${theme.custom?.shellSectionDivider}`,
            display: 'grid',
          }}
        >
          {section.children.map((child) => {
            const on = isLinkActive(child.link, pathname);
            return (
              <Box
                key={child.id ?? child.link}
                component={NavLink}
                to={child.link ?? '/'}
                onClick={onNavigate}
                aria-current={on ? 'page' : undefined}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  minHeight: 36,
                  padding: '0 10px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: on ? 600 : 500,
                  textDecoration: 'none',
                  color: on
                    ? theme.custom?.shellMenuItemCurrentText
                    : theme.custom?.shellMenuItemText,
                  background: on
                    ? theme.custom?.shellMenuItemCurrentBg
                    : 'transparent',
                  '&:hover': {
                    background: on
                      ? theme.custom?.shellMenuItemCurrentBg
                      : theme.custom?.shellMenuItemHoverBg,
                  },
                }}
              >
                {child.label}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export const SideMenu = ({
  open,
  onClose,
  items,
}: {
  open: boolean;
  onClose: () => void;
  items: SideBarMenuItem[];
}) => {
  const theme = useTheme();
  const { mainSections, pathname } = useNavModel(items);

  // Close after navigating, including browser back/forward.
  useEffect(() => {
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      slotProps={{
        backdrop: { sx: { backgroundColor: theme.custom?.shellScrim } },
        paper: {
          sx: {
            width: 300,
            maxWidth: 'calc(100vw - 40px)',
            borderRadius: '0 18px 18px 0',
            boxShadow: theme.custom?.shellSidePanelShadow,
          },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '18px 14px 8px 22px',
        }}
      >
        <Link to="/" onClick={onClose} aria-label="Home">
          <Icon name="LogoSVG" customSizes={{ width: 105, height: 32 }} />
        </Link>
        <ButtonBase
          onClick={onClose}
          aria-label="Close menu"
          sx={{
            width: 36,
            height: 36,
            borderRadius: '9px',
            color: theme.custom?.iconButton,
            '&:hover': { background: theme.custom?.iconButtonHover },
          }}
        >
          <NavIcon name="close" size={18} strokeWidth={1.8} />
        </ButtonBase>
      </Box>
      <Box
        component="nav"
        aria-label="Main menu"
        sx={{
          padding: '6px 12px 16px',
          display: 'grid',
          gap: '2px',
          alignContent: 'start',
          overflowY: 'auto',
        }}
      >
        {mainSections.map((section) => (
          <SideGroup
            key={section.item.id ?? section.item.label}
            section={section}
            pathname={pathname}
            onNavigate={onClose}
          />
        ))}
      </Box>
    </Drawer>
  );
};
